import type { HTMLAttributes, Ref } from 'react'
import { cx } from '@/shared/lib'
import s from './Divider.module.css'

export type DividerOrientation = 'horizontal' | 'vertical'
export type DividerTone = 'subtle' | 'default' | 'strong'
export type DividerSpacing = 'none' | 'xs' | 'sm' | 'md'
export type DividerInset = 'none' | 'sm' | 'md'
export type DividerLength = 'sm' | 'md' | 'stretch'

export type DividerProps = {
  /** Направление линии. Вертикальная работает внутри flex-строки. */
  orientation?: DividerOrientation
  /** Контраст: subtle — плотные списки и меню, strong — граница между зонами управления. */
  tone?: DividerTone
  /** Отступ поперёк линии, до и после неё. */
  spacing?: DividerSpacing
  /** Отступ вдоль линии от краёв контейнера. Для вертикальной игнорируется. */
  inset?: DividerInset
  /** Длина вертикальной линии. Для горизонтальной игнорируется. */
  length?: DividerLength
  /** Занять остаток flex-строки: заголовок секции плюс линия. Для вертикальной игнорируется. */
  grow?: boolean
  /** Линия только рисует и в дерево доступности не попадает. */
  decorative?: boolean
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'role' | 'aria-orientation'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<DividerTone, string | undefined> = {
  subtle: s.toneSubtle,
  default: s.toneDefault,
  strong: s.toneStrong,
}

const SPACING_CLASS: Record<DividerSpacing, string | undefined> = {
  none: undefined,
  xs: s.spacingXs,
  sm: s.spacingSm,
  md: s.spacingMd,
}

const INSET_CLASS: Record<DividerInset, string | undefined> = {
  none: undefined,
  sm: s.insetSm,
  md: s.insetMd,
}

const LENGTH_CLASS: Record<DividerLength, string | undefined> = {
  sm: s.lengthSm,
  md: s.lengthMd,
  stretch: s.lengthStretch,
}

/**
 * Линия между смысловыми группами: строками меню, блоками карточки, зонами тулбара.
 * Контракт: docs/ui-kit/contracts/divider.md
 */
export function Divider({
  orientation = 'horizontal',
  tone = 'default',
  spacing = 'none',
  inset = 'none',
  length = 'sm',
  grow = false,
  decorative = false,
  className,
  ref,
  ...rest
}: DividerProps) {
  const vertical = orientation === 'vertical'

  return (
    <div
      {...rest}
      ref={ref}
      className={cx(
        s.root,
        vertical ? s.vertical : s.horizontal,
        TONE_CLASS[tone],
        SPACING_CLASS[spacing],
        !vertical && INSET_CLASS[inset],
        vertical && LENGTH_CLASS[length],
        !vertical && grow && s.grow,
        className,
      )}
      role={decorative ? 'presentation' : 'separator'}
      aria-orientation={decorative ? undefined : orientation}
      aria-hidden={decorative || undefined}
    />
  )
}
